import {
  Node as INode,
  NodeId,
  Socket,
  SocketId,
  SocketDirection,
} from "./types";
import type { JSONSchema7 } from "json-schema";

export class Node implements INode {
  id: NodeId;
  type: string;
  position?: { x: number; y: number };
  metadata?: Record<string, unknown>;
  parameters?: Record<string, unknown>;

  // Private cache for performance - sockets should come from handlers/registry
  private inputs: Socket[];
  private outputs: Socket[];

  constructor(config: {
    id: NodeId;
    type: string;
    position?: { x: number; y: number };
    metadata?: Record<string, unknown>;
    parameters?: Record<string, unknown>;
  }) {
    this.id = config.id;
    this.type = config.type;
    this.position = config.position;
    this.metadata = config.metadata;
    this.parameters = config.parameters;
    // Sockets should be added via registry or addSocket methods
    this.inputs = [];
    this.outputs = [];
  }

  // Public getters for read-only access
  get inputSockets(): ReadonlyArray<Socket> {
    return this.inputs;
  }

  get outputSockets(): ReadonlyArray<Socket> {
    return this.outputs;
  }

  getSocket(socketId: SocketId): Socket | undefined {
    return [...this.inputs, ...this.outputs].find((s) => s.id === socketId);
  }

  getInputSocket(socketId: SocketId): Socket | undefined {
    return this.inputs.find((s) => s.id === socketId);
  }

  getOutputSocket(socketId: SocketId): Socket | undefined {
    return this.outputs.find((s) => s.id === socketId);
  }

  addInputSocket(socket: Omit<Socket, "nodeId" | "direction">): void {
    this.inputs.push({
      ...socket,
      nodeId: this.id,
      direction: SocketDirection.Input,
    });
  }

  addOutputSocket(socket: Omit<Socket, "nodeId" | "direction">): void {
    this.outputs.push({
      ...socket,
      nodeId: this.id,
      direction: SocketDirection.Output,
    });
  }

  // Helper methods for creating sockets with schemas
  static createSocket(
    id: string,
    schema: JSONSchema7,
    options?: {
      multiple?: boolean;
      required?: boolean;
      metadata?: Record<string, unknown>;
    },
  ): Omit<Socket, "nodeId" | "direction"> {
    return {
      id,
      schema,
      multiple: options?.multiple,
      required: options?.required,
      metadata: options?.metadata,
    };
  }

  // Common schema patterns
  static schemas = {
    string: (constraints?: Partial<JSONSchema7>): JSONSchema7 => ({
      type: "string",
      ...constraints,
    }),
    number: (constraints?: Partial<JSONSchema7>): JSONSchema7 => ({
      type: "number",
      ...constraints,
    }),
    boolean: (): JSONSchema7 => ({
      type: "boolean",
    }),
    array: (
      items: JSONSchema7,
      constraints?: Partial<JSONSchema7>,
    ): JSONSchema7 => ({
      type: "array",
      items,
      ...constraints,
    }),
    object: (
      properties: Record<string, JSONSchema7>,
      required?: string[],
    ): JSONSchema7 => ({
      type: "object",
      properties,
      required,
    }),
    any: (): JSONSchema7 => ({}), // Empty schema accepts anything
  };

  removeSocket(socketId: SocketId): boolean {
    const inputIndex = this.inputs.findIndex((s) => s.id === socketId);
    if (inputIndex !== -1) {
      this.inputs.splice(inputIndex, 1);
      return true;
    }

    const outputIndex = this.outputs.findIndex((s) => s.id === socketId);
    if (outputIndex !== -1) {
      this.outputs.splice(outputIndex, 1);
      return true;
    }

    return false;
  }

  toJSON(): INode {
    return {
      id: this.id,
      type: this.type,
      position: this.position,
      metadata: this.metadata,
    };
  }
}
